import {SupportedLanguageCode} from '../dropdown-options/language';
import {FnaQuestionnaireAnswers} from '../fna';
import {QuestionId} from '../insurance/question-id';
import {QuestionnaireAnswers} from '../questionnaire';
import {HealthAnswers} from '../questionnaire/health-answers';
import {WeightUnit} from '../questionnaire/weight';
import {bmi} from '../utils';
import {POUNDS_PER_KILOGRAM} from '../utils-const';
import {TranslatedAnswer, TranslatedOption} from './answer';
import {TranslatedSubtext, TranslatedUiQuestion, UiQuestion} from './ui-question';

export const FORCE_STOP = 'Force-Stop';
export const COMPLETE = 'Complete';

export interface Action {
  next: string | QuestionId;
  conditions?: Condition[];
}

export const conditionAppliers = {
  '=': (operand: any, value: any) => {
    if (typeof operand === 'string' && typeof value === 'string') {
      return operand.toLowerCase() === value.toLowerCase();
    }
    return operand === value;
  },
  '<': (operand: any, value: any) => operand < value,
  '>': (operand: any, value: any) => operand > value,
  '<=': (operand: any, value: any) => operand <= value,
  '>=': (operand: any, value: any) => operand >= value,
};

export interface Condition {
  operand: string;
  operator: '=' | '<' | '>' | '<=' | '>=';
  value: string | number | boolean;
}

export interface ConditionalText {
  value: TextTranslations;
  conditions?: Condition[];
}

export interface Subtext {
  type: SubtextType;
  texts: ConditionalText[];
}

export type AnswerType = 'RADIO' | 'YES_NO' | 'INPUT' | 'DROPDOWN';
export type SubtextType = 'BULLET' | 'TEXT';
export type InputType = 'TEXT' | 'TEXT_AREA' | 'NUMBER' | 'PHONE' | 'DATE';

export interface TextTranslations {
  fr: string;
  en: string;
}

export const translateText = (text: TextTranslations, lang: SupportedLanguageCode): string => text[lang];

export function translateUiQuestion<T extends QuestionnaireAnswers | FnaQuestionnaireAnswers = HealthAnswers>(
  uiQuestion: UiQuestion<T>,
  languageCode: SupportedLanguageCode,
): TranslatedUiQuestion<T> {
  const uiAnswer: TranslatedAnswer<T> = {
    propertyName: uiQuestion.answer.propertyName,
    type: uiQuestion.answer.type,
    value: uiQuestion.answer.value,
    label: uiQuestion.answer.label?.[languageCode],
    placeholder: uiQuestion.answer.placeholder?.[languageCode],
    prefix: uiQuestion.answer.prefix?.[languageCode],
    suffix: uiQuestion.answer.suffix?.[languageCode],
    inputType: uiQuestion.answer.inputType,
    options: uiQuestion.answer.options?.map((option): TranslatedOption => {
      return {text: option.text[languageCode], value: option.value};
    }),
  };

  if (!uiAnswer.options && uiAnswer.type === 'YES_NO') {
    uiAnswer.options = [
      {value: true, text: languageCode === 'fr' ? 'Oui' : 'Yes'},
      {value: false, text: languageCode === 'fr' ? 'Non' : 'No'},
    ];
  }

  return {
    id: uiQuestion.id,
    text: uiQuestion.text[languageCode],
    answer: uiAnswer,
    subtexts: uiQuestion.subtexts?.map((subtext): TranslatedSubtext => {
      return {type: subtext.type, value: subtext.value[languageCode]};
    }),
    extraQuestionnaire: uiQuestion.extraQuestionnaire,
    causesIneligibility: uiQuestion.causesIneligibility,
  };
}

export const transformers = {
  age: (answers: HealthAnswers) => {
    const today = new Date();
    const birthDate = new Date(answers.birthday as Date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  },

  bmi: (answers: HealthAnswers) => {
    const weightInKg =
      answers.weight?.unit === WeightUnit.KILOGRAMS
        ? answers.weight.value
        : answers.weight!.value / POUNDS_PER_KILOGRAM;
    return bmi(answers.height!, weightInKg);
  },
};

export const textAreaDefaultPlaceholder: TextTranslations = {
  fr: 'Écrire la réponse',
  en: 'Write the answer',
};
